/** Attribute on portaled ProSelect menus — allow touch scroll guards to pass through. */
export const PRO_SELECT_LISTBOX_ATTR = "data-pro-select-listbox";

export type ProSelectListboxPosition = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  position: "fixed" | "absolute";
};

const MENU_GAP_PX = 4;
const MENU_VIEWPORT_MARGIN_PX = 12;
const MENU_DEFAULT_MAX_HEIGHT_PX = 224;
const MENU_MIN_SCROLL_HEIGHT_PX = 120;

function resolveVisualViewportHeight(): number {
  return window.visualViewport?.height ?? window.innerHeight;
}

function clampMenuHeight(availableHeight: number): number {
  return Math.min(MENU_DEFAULT_MAX_HEIGHT_PX, Math.floor(availableHeight));
}

function resolveVerticalPlacement(
  rect: DOMRect,
  visibleTop: number,
  visibleBottom: number,
): { top: number; maxHeight: number } {
  const availableBelow = visibleBottom - rect.bottom - MENU_GAP_PX - MENU_VIEWPORT_MARGIN_PX;
  const availableAbove = rect.top - visibleTop - MENU_GAP_PX - MENU_VIEWPORT_MARGIN_PX;

  if (availableBelow >= MENU_MIN_SCROLL_HEIGHT_PX || availableBelow >= availableAbove) {
    return {
      top: rect.bottom + MENU_GAP_PX,
      maxHeight: Math.max(MENU_MIN_SCROLL_HEIGHT_PX, clampMenuHeight(availableBelow)),
    };
  }

  const maxHeight = Math.max(MENU_MIN_SCROLL_HEIGHT_PX, clampMenuHeight(availableAbove));
  return {
    top: rect.top - MENU_GAP_PX - maxHeight,
    maxHeight,
  };
}

/**
 * Radix/Vaul modals only deliver pointer events inside the dialog subtree.
 * Portaling to `document.body` paints on top but taps fall through to the drawer.
 */
export function resolveProSelectPortalRoot(anchor: HTMLElement | null): HTMLElement {
  if (!anchor) {
    return document.body;
  }

  return (
    anchor.closest<HTMLElement>("[data-pro-drawer]") ??
    anchor.closest<HTMLElement>("[data-radix-dialog-content]") ??
    anchor.closest<HTMLElement>('[role="dialog"]') ??
    document.body
  );
}

export function computeProSelectListboxPosition(
  trigger: HTMLElement,
  portalRoot: HTMLElement,
  menuMinWidth?: number,
): ProSelectListboxPosition {
  const rect = trigger.getBoundingClientRect();
  const width = Math.max(rect.width, menuMinWidth ?? 0);

  if (portalRoot === document.body) {
    const viewportHeight = resolveVisualViewportHeight();
    const vertical = resolveVerticalPlacement(rect, 0, viewportHeight);

    return {
      position: "fixed",
      top: vertical.top,
      left: rect.left,
      width,
      maxHeight: vertical.maxHeight,
    };
  }

  const rootRect = portalRoot.getBoundingClientRect();
  const viewportHeight = resolveVisualViewportHeight();
  const rootVisibleTop = Math.max(rootRect.top, 0);
  const rootVisibleBottom = Math.min(rootRect.bottom, viewportHeight);
  const vertical = resolveVerticalPlacement(rect, rootVisibleTop, rootVisibleBottom);

  return {
    position: "absolute",
    top: vertical.top - rootRect.top,
    left: rect.left - rootRect.left,
    width,
    maxHeight: vertical.maxHeight,
  };
}

export function isProSelectListboxTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(`[${PRO_SELECT_LISTBOX_ATTR}]`) !== null;
}
